import type { IdToken } from "@auth0/auth0-spa-js";
import * as Sentry from "@sentry/react";
import { type ReactNode, useCallback, useEffect, useState } from "react";
import { AuthContext, type IAuthContext } from "./AuthContext";
import { auth0Client, initAuthResult } from "./auth0Client";
import { useTrackContext } from "../tracking/TrackingContext";

export function Auth0AuthProvider({ children }: { children: ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [isInitialized, setIsInitialized] = useState(false);
  const [idTokenClaims, setIdTokenClaims] = useState<IdToken | undefined>();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const init = useCallback(async () => {
    setIsLoading(true);
    await initAuthResult;
    const isAuthenticated = await auth0Client.isAuthenticated();
    const idTokenClaims = await auth0Client.getIdTokenClaims();
    console.log({ isAuthenticated, idTokenClaims });
    setIsAuthenticated(isAuthenticated);
    setIdTokenClaims(idTokenClaims);
    setIsLoading(false);
    Sentry.setTag("isAuthenticated", isAuthenticated);
  }, []);

  useEffect(() => {
    async function waitForInitAuth() {
      setIsLoading(true);
      await init();
      setIsInitialized(true);
    }

    waitForInitAuth();
  }, [init]);

  useTrackContext("is_authenticated", isAuthenticated);

  const context: IAuthContext = {
    isLoading,
    isInitialized,
    email: idTokenClaims?.email ?? null,
    isAdmin: getIsAdmin(idTokenClaims),
    isAuthenticated,
  };

  return (
    <AuthContext.Provider value={context}>{children}</AuthContext.Provider>
  );
}

function getIsAdmin(idTokenClaims: IdToken | undefined) {
  if (!idTokenClaims) return false;

  const roles: string[] | undefined = idTokenClaims["https://id.naf.no/roles"];
  if (!roles) return false;

  const adminRole = "AccidentStatementAdmin";
  return roles.includes(adminRole);
}
